import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

export interface TxStatus {
  title: string;
  message: string | null;
  active: boolean;
}

export interface TxStatuses {
  pending: TxStatus;
  submitted: TxStatus;
  complete: TxStatus;
  error: TxStatus;
}

@Injectable({
  providedIn: 'root'
})
export class TransactionTrackingService {
  private txModalActiveSubject = new BehaviorSubject<boolean>(false);
  public txModalActive$ = this.txModalActiveSubject.asObservable();

  private txStatusSubject = new BehaviorSubject<TxStatuses>({
    pending: {
      title: 'Transaction Pending',
      message: null,
      active: false
    },
    submitted: {
      title: 'Transaction Submitted',
      message: null,
      active: false
    },
    complete: {
      title: 'Transaction Complete',
      message: null,
      active: false
    },
    error: {
      title: 'Transaction Error',
      message: null,
      active: false
    }
  });
  public txStatus$ = this.txStatusSubject.asObservable();

  get txModalActive(): boolean {
    return this.txModalActiveSubject.value;
  }

  get txStatus(): TxStatuses {
    return this.txStatusSubject.value;
  }

  get txStatusArray(): TxStatus[] {
    return Object.values(this.txStatusSubject.value);
  }

  initTransactionMessage(message?: string): void {
    this.txModalActiveSubject.next(true);
    this.clearTransaction();

    const currentStatus = this.txStatusSubject.value;
    currentStatus.pending.active = true;
    currentStatus.pending.message = message || '<p>Please submit your transaction using the connected Ethereum wallet.</p>';

    this.txStatusSubject.next({ ...currentStatus });
  }

  setTransactionMessage(hash: string, message?: string): void {
    this.txModalActiveSubject.next(true);
    this.clearTransaction();

    const currentStatus = this.txStatusSubject.value;
    currentStatus.submitted.active = true;

    const shortHash = `${hash.slice(0, 6)}...${hash.slice(-6)}`;
    currentStatus.submitted.message = (message || `
      <p>Your transaction is being processed on the Ethereum network.</p>
      <p>Transaction hash: <a href="https://etherscan.io/tx/${hash}" target="_blank">${shortHash}</a></p>
      <p>View it <a href="https://etherscan.io/tx/${hash}" target="_blank">here</a></p>
    `).replace(/\s+/g, ' ');

    this.txStatusSubject.next({ ...currentStatus });
  }

  setProcessTransactionMessage(receipt: any, message?: string): void {
    this.txModalActiveSubject.next(true);
    this.clearTransaction();

    const currentStatus = this.txStatusSubject.value;
    currentStatus.complete.active = true;

    const hash = receipt.transactionHash;
    const shortHash = `${hash.slice(0, 6)}...${hash.slice(-6)}`;
    currentStatus.complete.message = message || `
      <p>Your transaction is complete.</p>
      <p>Transaction hash: <a href="https://etherscan.io/tx/${hash}" target="_blank">${shortHash}</a></p>
      <p>View it <a href="https://etherscan.io/tx/${hash}" target="_blank">here</a></p>
    `.replace(/\s+/g, ' ');

    this.txStatusSubject.next({ ...currentStatus });
  }

  setErrorTransactionMessage(err: any, message?: string): void {
    this.txModalActiveSubject.next(true);
    this.clearTransaction();

    const currentStatus = this.txStatusSubject.value;
    console.log({err});

    currentStatus.error.active = true;

    const details = err.details || err.message;
    currentStatus.error.message = message || `
      <p>There was an error with your transaction:</p>
      <p class="error">${details}</p>
    `.replace(/\s+/g, ' ');

    this.txStatusSubject.next({ ...currentStatus });
  }

  closeTxModal(): void {
    this.txModalActiveSubject.next(false);
    this.clearTransaction();
  }

  clearTransaction(): void {
    const currentStatus = this.txStatusSubject.value;
    for (const status of Object.keys(currentStatus)) {
      currentStatus[status as keyof TxStatuses].active = false;
      currentStatus[status as keyof TxStatuses].message = null;
    }
    this.txStatusSubject.next({ ...currentStatus });
  }
}
